/**
 * Keycloak singleton + init helper.
 *
 * Why a module-level singleton: keycloak-js stores its session state
 * (tokens, refresh timers) in the JS object. Creating a fresh instance
 * on every React render would lose state + re-trigger silent SSO. The
 * single instance lives at module scope; the AuthProvider just calls
 * init() once on mount.
 *
 * Config (read from Vite env vars at build time):
 *   VITE_KEYCLOAK_URL       - issuer URL, e.g. http://localhost/auth
 *   VITE_KEYCLOAK_REALM     - marsad-core
 *   VITE_KEYCLOAK_CLIENT_ID - marsad-frontend (public client + PKCE S256)
 *
 * Production deployments override these via the runtime env-shim in
 * nginx or compose - same vars; build-time substitution from .env at
 * `npm run build` time, runtime override via /env.js for fully
 * configurable images is a future enhancement.
 *
 * Token refresh: keycloak-js auto-refreshes via onTokenExpired - we
 * just call updateToken(30) which refreshes if <30s left. Surfaced
 * through the api client's setAuthTokenGetter so every request reads
 * the freshest token without explicit plumbing.
 */
import Keycloak from "keycloak-js";

import { setAuthTokenGetter, setTokenRefresher } from "shared/api/client";
import { i18n, LANGUAGE_CHOSEN_KEY } from "shared/i18n";

interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

function readConfig(): KeycloakConfig {
  const env = import.meta.env;
  const url = env.VITE_KEYCLOAK_URL;
  const realm = env.VITE_KEYCLOAK_REALM;
  const clientId = env.VITE_KEYCLOAK_CLIENT_ID;
  if (!url || !realm || !clientId) {
    throw new Error(
      "Missing VITE_KEYCLOAK_* env vars. Set VITE_KEYCLOAK_URL + " +
        "VITE_KEYCLOAK_REALM + VITE_KEYCLOAK_CLIENT_ID in .env (see " +
        ".env.example for the docker-compose defaults).",
    );
  }
  return { url, realm, clientId };
}

// Module-scoped singleton. Initialised lazily in init() so unit tests
// importing this module don't trigger a real Keycloak request.
let _instance: Keycloak | null = null;

// In-flight init promise. Memoised so React 18 StrictMode's
// double-effect-in-dev does NOT call kc.init() twice on the same
// instance - keycloak-js v25 throws "A 'Keycloak' instance can only
// be initialized once." when that happens, which previously surfaced
// as a blank/error screen after the Keycloak redirect. Returning the
// same promise on the second call lets both StrictMode mounts resolve
// to the same auth state without poking kc.init() again.
let _initPromise: Promise<boolean> | null = null;

function getInstance(): Keycloak {
  if (_instance === null) {
    _instance = new Keycloak(readConfig());
  }
  return _instance;
}

/** True if the user has a live token; refreshed by keycloak-js. */
export function isAuthenticated(): boolean {
  return _instance?.authenticated === true;
}

/** A session that has ended goes to sign-in once, however many
 *  requests noticed it at the same moment. */
let _signingOut = false;
function signOutOnce(kc: {
  logout: (o: { redirectUri: string }) => Promise<void>;
}): void {
  if (_signingOut) return;
  _signingOut = true;
  void kc.logout({ redirectUri: window.location.origin });
}

/**
 * Initialise keycloak-js + wire the token getter so api/client picks
 * up live tokens. Returns true if the user is authenticated after
 * init (i.e. a valid SSO session was found); false if they need to
 * click login.
 *
 * onLoad: "check-sso" tries silent SSO via the third-party iframe; if
 * the iframe is blocked (Safari ITP, etc.), the user just sees the
 * unauthenticated state + the login button. No noisy errors.
 *
 * Idempotent: subsequent calls return the same in-flight (or settled)
 * promise. Required for React 18 StrictMode in dev where useEffect
 * fires twice on mount.
 */
export function initKeycloak(): Promise<boolean> {
  if (_initPromise !== null) {
    return _initPromise;
  }

  const kc = getInstance();

  // Wire the token getter BEFORE init so any in-flight requests during
  // first render don't race with an undefined token. The getter returns
  // null until init completes.
  setAuthTokenGetter(() => kc.token ?? null);
  // Round 20 K38 -- refreshed ahead of every request, not only when the
  // token has already lapsed. keycloak-js answers at once while the
  // token is good and shares one refresh between concurrent callers.
  setTokenRefresher(async (minValidity) => {
    if (!kc.authenticated) return;
    try {
      await kc.updateToken(minValidity);
    } catch (err) {
      signOutOnce(kc);
      throw err;
    }
  });

  // Auto-refresh: keycloak-js fires onTokenExpired when the access
  // token is within `minValidity` of expiring. Refresh proactively so
  // the next API call doesn't 401.
  kc.onTokenExpired = () => {
    void kc.updateToken(30).catch(() => {
      // Refresh failed (network blip, refresh-token expired). Force
      // re-login - the user sees the login button reappear.
      signOutOnce(kc);
    });
  };

  /* Round 17 K19 -- keycloak-js reports an application-initiated
     action's outcome while it parses the callback, inside init(). The
     only such action this product starts is the password step, so a
     success here is a password changed; it is held until the person is
     signed in, then reported once (AuthProvider). */
  kc.onActionUpdate = (status) => {
    if (status === "success") _passwordChanged = true;
  };

  _initPromise = kc
    .init({
      onLoad: "check-sso",
      pkceMethod: "S256",
      // The realm import sets webOrigins: "+" and silentCheckSsoRedirectUri
      // isn't required when the iframe is used directly. Keep the option
      // off so a misconfigured /silent-check-sso.html doesn't break init.
      checkLoginIframe: false,
    })
    .catch((err: unknown) => {
      // Don't cache a rejected init forever - clear so a manual retry
      // (the AuthErrorScreen 'Back to sign-in' reload) can try again
      // against a fresh page load.
      _initPromise = null;
      throw err;
    });

  return _initPromise;
}

/**
 * Round 17 I16 -- the identity provider's pages in the reader's
 * language. The realm offers English and Arabic (Keycloak's own
 * messages); a reader in Arabic, or its pseudo-locale, is sent as "ar".
 */
export function identityLocale(
  language: string | undefined = i18n.language,
): "ar" | "en" {
  return language?.startsWith("ar") ? "ar" : "en";
}

/**
 * Round 18 K23 -- the sign-in follows the browser until the reader picks
 * a language.
 *
 * The app sent `ui_locales` from its own language, which is English
 * outside development, so a browser set to Arabic got the English sign-in
 * and the page has no switcher: the Arabic pages were reachable only
 * through the pseudo-locale or a hand-made address. With no language
 * chosen in the app, nothing is sent, and the identity provider reads the
 * browser's Accept-Language. A reader who has chosen one is sent it.
 */
export function signInLocale(
  stored: string | null | undefined = readChosenLanguage(),
  language: string | undefined = i18n.language,
): "ar" | "en" | undefined {
  if (!stored) return undefined;
  return identityLocale(language);
}

function readChosenLanguage(): string | null {
  try {
    return globalThis.localStorage?.getItem?.(LANGUAGE_CHOSEN_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Trigger the Keycloak login redirect. */
export function login(): Promise<void> {
  // Pin redirectUri to the origin (not window.location.href). After
  // P1.AUTH.fix: returning to a path that already carries a stale
  // OIDC fragment/query confuses keycloak-js's callback parser - it
  // sees an old `code` AND the fresh one, picks the wrong one, and
  // the token exchange 400s. The origin is always clean.
  const locale = signInLocale();
  return getInstance().login({
    redirectUri: window.location.origin + "/",
    ...(locale ? { locale } : {}),
  });
}

/**
 * Change your own password, in the identity provider's own step.
 *
 * Keycloak runs its UPDATE_PASSWORD action (an application-initiated
 * action): the reader re-proves who they are, sets the new password
 * under the realm's password policy, and comes straight back. MARSAD
 * never sees either password and holds no credential that could set one
 * -- the position the product took on 2026-09-14 (users are administered
 * in the identity provider, not here) holds.
 */
export function changePassword(): Promise<void> {
  // Round 19 K35 -- the same rule as sign-in: with no app language
  // chosen, send none, so the step follows the browser. It sent the
  // app's language, English outside development, and a reader who signed
  // in in Arabic was asked for a new password in English.
  const locale = signInLocale();
  return getInstance().login({
    action: "UPDATE_PASSWORD",
    redirectUri: window.location.origin + "/",
    ...(locale ? { locale } : {}),
  });
}

/**
 * The identity provider a person signed in through, where it was not
 * this realm's own password (the realm's `idp` claim, set by a session
 * note mapper on the client). Null for a local account.
 *
 * Such a person has no password here to change: theirs is held by their
 * organisation's provider, and offering "Change password" would let them
 * create a second, local one beside it.
 */
export function brokeredBy(
  payload: Record<string, unknown> | null = tokenPayload(),
): string | null {
  const idp = payload?.idp;
  return typeof idp === "string" && idp.trim() !== "" ? idp : null;
}

/** Trigger logout + redirect home. */
export function logout(): Promise<void> {
  return getInstance().logout({
    redirectUri: window.location.origin,
  });
}

/** The parsed JWT payload, if authenticated. Read-only convenience. */
export function tokenPayload(): Record<string, unknown> | null {
  return (_instance?.tokenParsed as Record<string, unknown> | undefined) ?? null;
}

let _passwordChanged = false;

/** Round 17 K19 -- true once, after the password step returned with
 *  success; reading it clears it, so the report is made once. */
export function takePasswordChanged(): boolean {
  const changed = _passwordChanged;
  _passwordChanged = false;
  return changed;
}

/** TEST-ONLY: reset the singleton + memoised init between vitest runs. */
export function _resetForTests(): void {
  _instance = null;
  _initPromise = null;
  _passwordChanged = false;
}
